/**
 * Contour editing maths (PredictionObject.Contours.cs): slice a segmentation mesh with the
 * contour plane into a ring, project a dragged edge handle radially onto the surface, tilt the
 * plane from a held handle about the centerline, and keep the three handles on ring samples.
 * Scene mm throughout; the meshes' geometries are already in scene mm.
 */
import * as THREE from 'three';
import { closestPointOnPolyline } from './geometry';

/** The plane crossings of each triangle as a segment (two distinct points), unordered. */
export function sliceMeshSegments(meshes: THREE.Mesh[], point: THREE.Vector3, normal: THREE.Vector3): [THREE.Vector3, THREE.Vector3][] {
  const out: [THREE.Vector3, THREE.Vector3][] = [];
  const n = normal.clone().normalize();
  const v = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  const d = [0, 0, 0];
  const tri: THREE.Vector3[] = [];
  const edge = (a: THREE.Vector3, b: THREE.Vector3, da: number, db: number) => {
    if (Math.sign(da) === Math.sign(db) && Math.abs(da) > 1e-6 && Math.abs(db) > 1e-6) return;
    const denom = da - db;
    if (Math.abs(denom) < 1e-6) return;
    const t = da / denom;
    if (t < -1e-6 || t > 1 + 1e-6) return;
    const p = a.clone().lerp(b, t);
    if (tri.some((q) => q.distanceToSquared(p) < 1e-10)) return; // a vertex on the plane counts once
    tri.push(p);
  };
  for (const mesh of meshes) {
    const g = mesh.geometry;
    const pos = g.getAttribute('position');
    if (!pos) continue;
    const index = g.getIndex();
    const count = index ? index.count : pos.count;
    // SurfaceSet geometries are already in scene mm (FILE_TO_SCENE applied at load), and the
    // meshes sit at identity under their container, so vertices are used as stored.
    for (let i = 0; i + 2 < count; i += 3) {
      for (let k = 0; k < 3; k++) {
        const vi = index ? index.getX(i + k) : i + k;
        v[k].set(pos.getX(vi), pos.getY(vi), pos.getZ(vi));
        d[k] = (v[k].x - point.x) * n.x + (v[k].y - point.y) * n.y + (v[k].z - point.z) * n.z;
      }
      tri.length = 0;
      edge(v[0], v[1], d[0], d[1]);
      edge(v[1], v[2], d[1], d[2]);
      edge(v[2], v[0], d[2], d[0]);
      if (tri.length === 2) out.push([tri[0], tri[1]]); // 3 = coplanar triangle, 1 = a touching vertex
    }
  }
  return out;
}

/** ComputeMeshPlaneIntersection: every triangle-edge crossing of the plane, unordered. */
export function sliceMeshWithPlane(meshes: THREE.Mesh[], point: THREE.Vector3, normal: THREE.Vector3): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  for (const [a, b] of sliceMeshSegments(meshes, point, normal)) out.push(a, b);
  return out;
}

/**
 * Chain the crossing segments into polylines by shared endpoints (triangles sharing an edge
 * cross the plane at the same point). A plane through a whole aorta mesh usually cuts it in more
 * than one place (the root and, further along, the arch or the LV), and sorting every crossing
 * by angle about one centre interleaves those loops into a zigzag; chaining keeps each loop
 * intact so the caller can pick the one around the contour centre.
 */
export function chainSegments(segments: [THREE.Vector3, THREE.Vector3][]): THREE.Vector3[][] {
  const key = (p: THREE.Vector3) => `${Math.round(p.x * 1e3)},${Math.round(p.y * 1e3)},${Math.round(p.z * 1e3)}`;
  const nodes = new Map<string, number[]>(); // endpoint key -> segment indices
  const ends = segments.map(([a, b]) => [key(a), key(b)] as const);
  const used = new Array<boolean>(segments.length).fill(false);
  // A plane through a vertex row yields each row edge twice (the triangles above and below);
  // the duplicate would turn the walk back on itself, so it is dropped.
  const seen = new Set<string>();
  ends.forEach(([ka, kb], i) => {
    const pair = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
    if (ka === kb || seen.has(pair)) {
      used[i] = true;
      return;
    }
    seen.add(pair);
    (nodes.get(ka) ?? nodes.set(ka, []).get(ka)!).push(i);
    (nodes.get(kb) ?? nodes.set(kb, []).get(kb)!).push(i);
  });
  const chains: THREE.Vector3[][] = [];
  const walk = (startKey: string, startSeg: number): { points: THREE.Vector3[]; endKey: string } => {
    const points: THREE.Vector3[] = [];
    let seg = startSeg;
    let at = startKey;
    for (;;) {
      used[seg] = true;
      const [ka, kb] = ends[seg];
      const [pa, pb] = segments[seg];
      const next = ka === at ? kb : ka;
      points.push(ka === at ? pb.clone() : pa.clone());
      at = next;
      const candidates = (nodes.get(at) ?? []).filter((s) => !used[s]);
      if (!candidates.length) return { points, endKey: at };
      seg = candidates[0];
    }
  };
  for (let i = 0; i < segments.length; i++) {
    if (used[i]) continue;
    const forward = walk(ends[i][0], i); // starts with segment i's far end
    let chain = [segments[i][0].clone(), ...forward.points];
    if (forward.endKey !== ends[i][0]) {
      // an open chain (mesh boundary): extend from the start end backwards too
      const back = (nodes.get(ends[i][0]) ?? []).filter((s) => !used[s]);
      if (back.length) chain = [...walk(ends[i][0], back[0]).points.reverse(), ...chain];
    } else chain.pop(); // closed: the last point repeats the first
    chains.push(chain);
  }
  return chains;
}

/** Orient a loop counter-clockwise about `normal` (the order SortPointsInPlane produces). */
function orientLoop(loop: THREE.Vector3[], normal: THREE.Vector3): THREE.Vector3[] {
  const n = new THREE.Vector3();
  for (let i = 0; i < loop.length; i++) {
    const a = loop[i];
    const b = loop[(i + 1) % loop.length];
    n.x += (a.y - b.y) * (a.z + b.z);
    n.y += (a.z - b.z) * (a.x + b.x);
    n.z += (a.x - b.x) * (a.y + b.y);
  }
  return n.dot(normal) < 0 ? loop.slice().reverse() : loop;
}

/** SortPointsInPlane: order by signed angle about the mean, in the plane. */
export function sortPointsInPlane(points: THREE.Vector3[], normal: THREE.Vector3): THREE.Vector3[] {
  if (points.length < 3) return points.slice();
  const n = normal.clone().normalize();
  const center = new THREE.Vector3();
  for (const p of points) center.add(p);
  center.multiplyScalar(1 / points.length);
  let ref = points[0].clone().sub(center).projectOnPlane(n);
  if (ref.lengthSq() < 1e-6) ref = new THREE.Vector3(1, 0, 0).projectOnPlane(n);
  if (ref.lengthSq() < 1e-6) ref = new THREE.Vector3(0, 1, 0).projectOnPlane(n);
  ref.normalize();
  const side = new THREE.Vector3().crossVectors(n, ref);
  return points
    .map((p) => {
      const dir = p.clone().sub(center).projectOnPlane(n);
      return { p, angle: Math.atan2(dir.dot(side), dir.dot(ref)) };
    })
    .sort((a, b) => a.angle - b.angle)
    .map((x) => x.p);
}

/**
 * Re-slice a contour: the crossing loop whose centroid lies nearest `point` (the contour
 * centre), oriented about the normal, plus its mean. Null when the plane misses. Falls back to
 * the angle sort of every crossing when no loop can be chained (a degenerate mesh).
 */
export function sliceContour(meshes: THREE.Mesh[], point: THREE.Vector3, normal: THREE.Vector3): { ring: THREE.Vector3[]; mean: THREE.Vector3 } | null {
  const segments = sliceMeshSegments(meshes, point, normal);
  if (segments.length < 2) return null;
  const meanOf = (pts: THREE.Vector3[]) => {
    const m = new THREE.Vector3();
    for (const p of pts) m.add(p);
    return m.multiplyScalar(1 / pts.length);
  };
  let best: { ring: THREE.Vector3[]; mean: THREE.Vector3; d: number } | null = null;
  for (const loop of chainSegments(segments)) {
    if (loop.length < 3) continue;
    const mean = meanOf(loop);
    const d = mean.distanceToSquared(point);
    if (!best || d < best.d) best = { ring: orientLoop(loop, normal), mean, d };
  }
  if (best) return { ring: best.ring, mean: best.mean };
  const crossings = sliceMeshWithPlane(meshes, point, normal);
  if (crossings.length < 3) return null;
  const ring = sortPointsInPlane(crossings, normal);
  return { ring, mean: meanOf(ring) };
}

/**
 * ProjectPointToAortaSurface: cast from the centre along +/- the radial direction of the
 * candidate and take the hit nearest the candidate; without a hit the candidate stays.
 * `raycast` casts in scene space and returns hit points.
 */
export function projectRadialToSurface(center: THREE.Vector3, candidate: THREE.Vector3, fallbackDir: THREE.Vector3, raycast: (origin: THREE.Vector3, dir: THREE.Vector3, maxDistance: number) => THREE.Vector3[]): THREE.Vector3 {
  let radial = candidate.clone().sub(center);
  if (radial.lengthSq() < 1e-8) radial = fallbackDir.clone();
  radial.normalize();
  const maxDistance = Math.max(50, candidate.distanceTo(center) * 4);
  let best: THREE.Vector3 | null = null;
  let bestD = Infinity;
  for (const dir of [radial, radial.clone().negate()]) {
    for (const hit of raycast(center, dir, maxDistance)) {
      const d = hit.distanceToSquared(candidate);
      if (d < bestD) {
        bestD = d;
        best = hit;
      }
    }
  }
  return best ?? candidate.clone();
}

/**
 * TryComputeDraggedContourHandleTiltNormal: a held edge handle tilts the plane about the
 * centerline instead of re-fitting it through all three handles. The pivot axis is
 * cross(tangent, radial); the plane keeps passing through the handle.
 */
export function tiltNormalFromHandle(center: THREE.Vector3, currentNormal: THREE.Vector3, tangent: THREE.Vector3, handle: THREE.Vector3): THREE.Vector3 | null {
  const t = tangent.clone().normalize();
  const toHandle = handle.clone().sub(center);
  if (toHandle.lengthSq() < 1e-10) return null;
  const radial = toHandle.clone().sub(t.clone().multiplyScalar(toHandle.dot(t)));
  if (radial.lengthSq() < 1e-4 * toHandle.lengthSq()) return null;
  const axis = new THREE.Vector3().crossVectors(t, radial.clone().normalize());
  if (axis.lengthSq() < 1e-8) return null;
  axis.normalize();
  const current = currentNormal.clone().normalize();
  const alongAxis = current.dot(axis);
  const inPlane = current.clone().sub(axis.clone().multiplyScalar(alongAxis));
  const inPlaneMag = inPlane.length();
  if (inPlaneMag < 1e-4) return null;
  const swept = new THREE.Vector3().crossVectors(axis, toHandle);
  if (swept.lengthSq() < 1e-10) return null;
  swept.normalize();
  if (swept.dot(inPlane) < 0) swept.negate();
  const normal = axis.multiplyScalar(alongAxis).add(swept.multiplyScalar(inPlaneMag)).normalize();
  return normal.lengthSq() > 0.5 ? normal : null;
}

/** The 3-point normal of the handles, sign-matched to the current normal. */
export function handlesNormal(handles: THREE.Vector3[], currentNormal: THREE.Vector3): THREE.Vector3 | null {
  const n = new THREE.Vector3().crossVectors(handles[1].clone().sub(handles[0]), handles[2].clone().sub(handles[0]));
  if (n.lengthSq() < 1e-8) return null;
  n.normalize();
  if (n.dot(currentNormal) < 0) n.negate();
  return n;
}

function closestRingIndex(ring: THREE.Vector3[], p: THREE.Vector3, used: Set<number>, minGap: number): number {
  let best = -1;
  let bestD = Infinity;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    let ok = true;
    for (const u of used) {
      const gap = Math.min(Math.abs(i - u), n - Math.abs(i - u));
      if (gap < minGap) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    const d = ring[i].distanceToSquared(p);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** ConstrainSTJControlPointsToRing: each handle onto its nearest ring sample, at least
 * max(2, N/10) samples apart, the dragged handle first. Returns the sample indices. */
export function constrainHandlesToRing(ring: THREE.Vector3[], handles: THREE.Vector3[], draggedIndex: number | null): number[] | null {
  const n = ring.length;
  if (n < 6 || handles.length < 3) return null;
  const minGap = Math.max(2, Math.floor(n / 10));
  const used = new Set<number>();
  const resolved = [-1, -1, -1];
  const order = draggedIndex !== null ? [draggedIndex, ...[0, 1, 2].filter((i) => i !== draggedIndex)] : [0, 1, 2];
  for (const i of order) {
    let idx = closestRingIndex(ring, handles[i], used, minGap);
    if (idx < 0) idx = closestRingIndex(ring, handles[i], new Set(), 0);
    resolved[i] = Math.max(0, idx);
    used.add(resolved[i]);
  }
  return resolved;
}

/** The three initial handle samples of a ring: indices 0, N/3 and 2N/3. */
export function initialHandleIndices(count: number): [number, number, number] {
  return [0, Math.floor(count / 3), Math.floor((count * 2) / 3)];
}

/** Rail projection of the centre handle (the AortaCurve, else the Root Centroid). */
export function projectOntoRail(rail: THREE.Vector3[], p: THREE.Vector3): { point: THREE.Vector3; tangent: THREE.Vector3 } | null {
  if (rail.length < 2) return null;
  const c = closestPointOnPolyline(rail, p);
  const a = rail[Math.max(0, c.segment)];
  const b = rail[Math.min(rail.length - 1, c.segment + 1)];
  const tangent = b.clone().sub(a);
  if (tangent.lengthSq() < 1e-10) return null;
  return { point: c.point, tangent: tangent.normalize() };
}
